import React, { useState } from 'react';

import { Checkbox, SelectButtons } from '../../';
import { useQueryParams } from '../../../hooks';

/**
 * One filter control in a table: a checkbox in the toolbar, or a row of buttons above the table.
 *
 * The control keeps its own value and remembers it — in the browser history always, and in local
 * storage when asked — so a view that only wants a filter does not have to hold state for it. Pass
 * `value` to take that over: the control then shows what it is given and reports changes without
 * remembering anything, which is what a caller that owns the value and its place in the URL needs.
 *
 * Note (JOHAN G):
 *   - adjustment: stringify value when setting item in local storage
 *   - adjustment: parse value when getting item from local storage
 * The adjustments assumes we only handle checkbox
 *
 * @param {object} props
 * @param {'checkbox'|'select-buttons'} props.type - Which control to render.
 * @param {string} props.name                      - Field name; the key the value is remembered under.
 * @param {string} [props.label]                   - Label text.
 * @param {string} [props.tooltip]                 - Tooltip description.
 * @param {function} [props.onChange]              - Called with `{ [name]: value }`.
 * @param {*} [props.value]                        - Makes the control controlled; see above.
 * @param {object[]} [props.options]               - select-buttons: the options to offer.
 * @param {string} [props.design]                  - select-buttons: SelectButtons design variant.
 * @param {string} [props.className]               - Additional CSS class names.
 * @param {*} [props.defaultValue]                 - Value to start from when nothing is remembered.
 * @param {string[]} [props.filterObjects]         - Re-runs the table's filter when the value changes.
 * @param {function} [props.requestFilter]         - The table's filter callback.
 * @param {boolean} [props.saveInLocalStorage=false] - Remember the value across sessions.
 */
export const CustomFilterControl = ({
  type,
  name,
  label,
  tooltip,
  onChange,
  value,
  options,
  design,
  className,
  filterObjects,
  defaultValue = undefined,
  requestFilter,
  saveInLocalStorage = false,
}) => {
  const { queryParams, setHistory } = useQueryParams();
  // A control given a value is driven by its caller, who is then also the one remembering it.
  const isControlled = value !== undefined;
  const [ownValue, setOwnValue] = useState(
    queryParams.filterControls[name] ||
      (saveInLocalStorage && localStorage.getItem(name) === 'true') ||
      defaultValue
  );
  const controlValue = isControlled ? value : ownValue;

  const handleChange = event => {
    let { name, value } = '';

    // Handle standard form inputs
    if (event.target !== undefined) {
      name = event.target.name;
      value = event.target.value;

      // If input element is a checkbox, we cannot use "value"
      if (event.target.type === 'checkbox') {
        value = event.target.checked;
      }

      // Handle custom form inputs
    } else {
      name = event.name;
      value = event.value;
    }

    const res = { [name]: value };

    if (!isControlled) setOwnValue(value);

    onChange && onChange(res);
    filterObjects && requestFilter(undefined); //, res)

    if (isControlled) return;

    // Persist in local storage
    saveInLocalStorage && localStorage.setItem(name, JSON.stringify(value));

    // Add to browser history
    setHistory({
      filterControls: {
        ...(queryParams.filterControls[name] || {}),
        [name]: value,
      },
    });
  };

  let element;
  switch (type) {
    case 'checkbox':
      element = (
        <Checkbox
          name={name}
          label={label}
          title={tooltip}
          checked={controlValue}
          onChange={handleChange}
        />
      );
      break;
    case 'select-buttons':
      element = (
        <SelectButtons
          name={name}
          label={label}
          title={tooltip}
          value={controlValue}
          options={options}
          design={design}
          className={className}
          onChange={handleChange}
        />
      );
      break;
    default:
      break;
  }
  return element;
};
